import React, { useState } from 'react';
import dayjs from 'dayjs';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useSitrepTemplatesV2 } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { AddUpdateAor } from 'components/GroupsRoute/AddUpdateAor';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import {
  SitrepCardSurface,
  type GetSitrepsResponse,
} from 'components/Reports/reports.types';
import { useAors } from 'hooks/useAors';

type SitrepCardHeaderProps = {
  sitrep: GetSitrepsResponse;
  incidentName: string | null;
  aorName: string | null;
  surface?: SitrepCardSurface;
};

/** Shared header chrome for all SITREP card surfaces. */
const SitrepCardHeader: React.FC<SitrepCardHeaderProps> = ({
  sitrep,
  incidentName,
  aorName,
  surface,
}) => {
  const [showEditAor, setShowEditAor] = useState(false);
  const { isMapVisible } = useUscgMapContext();
  const { data: templates } = useSitrepTemplatesV2();
  const { data: aors } = useAors();

  const template = templates?.find((t) => t.id === sitrep.sitrep_template_id);
  const reportLabel = template?.name?.toUpperCase().includes('OPORD')
    ? 'OPORD'
    : 'REPORT';

  const editableAor = sitrep.aor_id
    ? (aors?.find((a) => a.id === sitrep.aor_id) ?? null)
    : null;

  const isPendingReviewDraft =
    surface === SitrepCardSurface.Draft && sitrep.approval_status === 'PENDING';

  const statusConfig =
    surface === SitrepCardSurface.Review
      ? {
          key: 'pending-approval',
          label: 'Pending Approval',
        }
      : isPendingReviewDraft
        ? {
            key: 'pending-review',
            label: sitrep.pending_reviewer_names
              ? `Pending review by ${sitrep.pending_reviewer_names}`
              : 'Pending review',
          }
        : surface === SitrepCardSurface.Draft
          ? {
              key: 'draft',
              label: 'Draft',
            }
          : null;

  return (
    <div className="d-flex flex-column">
      <div className="txt--semibold txt--lg">
        {reportLabel}: {incidentName || aorName || ''}
      </div>
      <div className="txt--sml">
        {sitrep.approvals?.[0] && (
          <>
            Approved by: {sitrep.approvals[0].approved_by_user_name} at{' '}
            {dayjs(sitrep.approvals[0].approved_at).format('MMM DD, HH:mm')}
          </>
        )}
        {(statusConfig || aorName) && (
          <div className="d-flex align-items-center mt-1 flex-wrap gap-2">
            {statusConfig && (
              <Badge variant={statusConfig.key === 'draft' ? 'orange' : 'cyan'}>
                {statusConfig.label}
              </Badge>
            )}
            {aorName && <Badge variant="outline">AOR: {aorName}</Badge>}
            {editableAor && isMapVisible && (
              <span
                role="presentation"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Button
                  variant="outline"
                  size="xs"
                  type="button"
                  onClick={() => setShowEditAor(true)}
                >
                  Edit AOR
                </Button>
                <AddUpdateAor
                  show={showEditAor}
                  onClose={() => setShowEditAor(false)}
                  mode="Edit"
                  editAorData={editableAor}
                />
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SitrepCardHeader;
